// src/games/CrossWords/base.tsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IoTriangleSharp } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import { GameHeader } from '../../components/GameHeader';
import { Sidebar } from '../../components/Sidebar';
import { usePopup } from '../../hooks/popup';
import { FinishModal } from '../core/finish/FinishModal';
import { revealConfirm } from '../core/reveal/revealConfirm';
import { FinishResult, useGameSession } from '../core/session/useGameSession';
import { PassatempoDetail, saveProgresso, verificar } from './api';
import Board from './components/Board';
import { GameWrapper } from './components/Board/styles';
import { Clues } from './components/Clues';
import Keyboard from './components/Keyboard';
import { GameContext } from './context/GameContext';
import type { iSquareSaved } from './context/interfaces';
import {
  allPlayableCells,
  buildCrosswordCache,
  buildVerifyRequest,
  countCorrectCrossword,
  maxCrosswordScore,
  revealCells,
  scoreCrossword,
} from './rules';
import { Container, Content, GameContent } from './styles';

interface BaseProps {
  id: number;
  direct: boolean;
  size: number;
  sizeHeight: number;
  gameDetail: PassatempoDetail;
  savedSeconds: number;
  alreadyFinished: FinishResult | null;
}

const BaseCrossWords: React.FC<BaseProps> = ({ id, direct, gameDetail, size, sizeHeight, savedSeconds, alreadyFinished }) => {
  const navigate = useNavigate();
  const { state, dispatch } = useContext(GameContext);
  const { GameTemplate } = state;
  const { mountPopup } = usePopup();
  const [letter, setLetter] = useState('');

  const maxScore = useMemo(() => maxCrosswordScore(GameTemplate), [GameTemplate]);

  // O reducer muta a matriz no lugar: a ref sempre lê o estado mais novo.
  const stateRef = useRef(state);
  stateRef.current = state;
  const currentMatrix = useCallback(() => stateRef.current.GameState.savedGame.cache.matrix, []);

  // Depois de tentar verificar, a sessão não salva mais parcial (nem se o `verificar` falhar).
  const session = useGameSession({
    ready: true,
    savedSeconds,
    alreadyFinished,
    onSaveProgress: (seconds) => saveProgresso(id, buildCrosswordCache(GameTemplate, currentMatrix(), seconds)),
  });
  const { finish, isPlaying } = session;
  const playingRef = useRef(isPlaying);
  playingRef.current = isPlaying;

  // Reaberta já verificada: pinta acertos/erros; o jogo fica só para leitura.
  useEffect(() => {
    if (alreadyFinished) dispatch({ type: 'CHECK_GAME' });
  }, [alreadyFinished, dispatch]);

  useEffect(() => {
    // Evita letra dupla (comportamento de antes).
    if (letter === '') return;
    dispatch({ type: 'INSERT', payload: { value: letter } });
    setTimeout(() => setLetter(''), 200);
  }, [dispatch, letter]);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (!playingRef.current) return;
    const key = event.key.toUpperCase();
    if (key === 'BACKSPACE' || key === 'DELETE') return;
    if (/^[a-zA-Z]$/.test(event.key)) setLetter(event.key);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!playingRef.current) return;
      const key = event.key.toUpperCase();
      if (key === 'BACKSPACE' || key === 'DELETE') dispatch({ type: 'HANDLE_BACKSPACE' });
    },
    [dispatch]
  );

  const handleVirtualKeyPress = useCallback(
    (key: string) => {
      if (!playingRef.current) return;
      if (key === '⌫') dispatch({ type: 'HANDLE_BACKSPACE' });
      else setLetter(key);
    },
    [dispatch]
  );

  useEffect(() => {
    window.addEventListener('keypress', handleKeyPress);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, handleKeyPress]);

  const finishWith = useCallback(
    (matrix: iSquareSaved[][]) => {
      if (!playingRef.current) return;
      dispatch({ type: 'CHECK_GAME' });
      const local: FinishResult = {
        points: scoreCrossword(GameTemplate, matrix),
        maxPoints: maxScore,
        correct: countCorrectCrossword(GameTemplate, matrix),
        total: maxScore,
      };
      void finish(local, async (seconds) => {
        const res = await verificar(id, buildVerifyRequest(GameTemplate, matrix, seconds));
        return { points: res.acertos, maxPoints: res.total };
      });
    },
    [GameTemplate, dispatch, finish, id, maxScore]
  );

  const askVerify = () =>
    mountPopup({
      title: 'Verificar acertos?',
      message: 'O jogo termina e o resultado vale para o ranking.',
      type: 'alegre',
      buttons: [
        { buttonText: 'Verificar', buttonFunction: () => finishWith(currentMatrix()) },
        { buttonText: 'Cancelar', isPurple: true },
      ],
    });

  const askRevealLetter = () =>
    mountPopup(
      revealConfirm({ scope: 'cell', unit: 'casa', title: 'Revelar letra?', onConfirm: () => dispatch({ type: 'REVEAL_LETTER' }) })
    );

  const askRevealWord = () =>
    mountPopup(
      revealConfirm({ scope: 'word', unit: 'casa', title: 'Revelar palavra?', onConfirm: () => dispatch({ type: 'REVEAL_WORD' }) })
    );

  const askRevealAll = () =>
    mountPopup(
      revealConfirm({
        scope: 'all',
        unit: 'casa',
        title: 'Revelar resposta?',
        onConfirm: () => {
          // Recusado fora de 'playing': não mexe na grade.
          if (!playingRef.current) return;
          // Mesmo cálculo do reducer, feito aqui para terminar com a grade revelada.
          const revealed = revealCells(GameTemplate, currentMatrix(), allPlayableCells(GameTemplate));
          dispatch({ type: 'REVEAL_ALL_GAME' });
          finishWith(revealed);
        },
      })
    );

  const askClear = () =>
    mountPopup({
      title: 'Limpar o jogo?',
      message: 'As letras que você digitou serão apagadas. Casas reveladas continuam reveladas.',
      type: 'atencao',
      buttons: [
        { buttonText: 'Limpar', buttonFunction: () => dispatch({ type: 'CLEAR_GAME' }) },
        { buttonText: 'Cancelar', isPurple: true },
      ],
    });

  const menuItems = isPlaying
    ? [
        { buttonText: 'Verificar acertos', buttonFunctions: askVerify },
        { buttonText: 'Revelar letra', buttonFunctions: askRevealLetter },
        { buttonText: 'Revelar palavra', buttonFunctions: askRevealWord },
        { buttonText: 'Revelar resposta', buttonFunctions: askRevealAll },
        { buttonText: 'Limpar jogo', buttonFunctions: askClear },
      ]
    : [{ buttonText: 'Ver resultado', buttonFunctions: session.showResult }];

  const currentScore = session.result?.points ?? 0;

  return (
    <Container>
      <GameHeader
        type="CRUZADAS"
        menuItems={menuItems}
        maxScore={maxScore}
        currentScore={currentScore}
        seconds={session.seconds}
      />

      <Content>
        <GameContent>
          <div>
            <GameWrapper>
              <Board
                gameID={id}
                size={size}
                sizeHeight={sizeHeight}
                text={gameDetail.texto ?? ''}
                title={gameDetail.titulo ?? ''}
                direct={direct}
              />
            </GameWrapper>
            <Sidebar
              type="CRUZADAS"
              menuItems={menuItems}
              maxScore={maxScore}
              currentScore={currentScore}
              seconds={session.seconds}
            />
          </div>
          {!direct && <Clues />}
        </GameContent>

        <Keyboard handleKeyPress={handleVirtualKeyPress} />
        <p style={{ background: '#7f4788' }}>
          VER MAIS COMENTÁRIOS <IoTriangleSharp size={12} />
        </p>
      </Content>

      <FinishModal
        visible={session.modalVisible}
        result={session.result}
        seconds={session.seconds}
        error={session.error}
        busy={session.status === 'finishing'}
        onRetry={session.retry}
        onClose={session.hideResult}
        onHome={() => navigate('/')}
      />
    </Container>
  );
};

export default BaseCrossWords;
